import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { costCenterService, orderClientService } from '../../../api/apiService';
import { useCostCenters } from '../../../contexts/CostCenterContext';
import EditableList from '../../Admin/EditableList';
import '../../../App.css';
import './SettingsPage.css';

/**
 * Superadmin Settings (/admin/settings).
 *
 * Manages the two DB-backed lists that used to live in source files:
 *   - cost centers (Kostenstellen = type 1, Kostenträger = type 2)
 *   - order clients of the typed order forms (KIND, Copywrite), each with optional default cost centers
 * and links to the Google Sheets export settings.
 *
 * Nothing on this page deletes. Cost centers and clients are referenced by historical bookings
 * and printed on credit notes, so "removing" one only sets isActive = false: it disappears from
 * the dropdowns while every old record keeps its value. Reactivation is always possible.
 */

const TABS = [
  { key: 'cost-centers', label: 'Cost centers' },
  { key: 'order-clients', label: 'Order clients' },
  { key: 'sheets-export', label: 'Sheets export' },
];

const STORAGE_KEY = 'settings.activeTab';

const readStoredTab = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return TABS.some((t) => t.key === v) ? v : TABS[0].key;
  } catch {
    return TABS[0].key;
  }
};

const storeTab = (key) => {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // localStorage unavailable (private mode, quota) — the tab simply is not remembered
  }
};

const apiError = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** Only the keys whose value differs from the row are sent, so an unchanged number is never re-validated as a duplicate. */
const changedOnly = (row, values, normalise) =>
  Object.keys(values).reduce((acc, key) => {
    const next = normalise(key, values[key]);
    const prev = normalise(key, row[key]);
    if (next !== prev) acc[key] = next;
    return acc;
  }, {});

const trimOrNull = (v) => {
  const s = v === null || v === undefined ? '' : String(v).trim();
  return s === '' ? null : s;
};

const numberOrNull = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// Sorted the same way the dropdowns show them
const bySort = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.label).localeCompare(String(b.label));

// ── Cost centers ──────────────────────────────────────────────────────────────

const COST_CENTER_TYPES = [
  { type: 1, label: 'Kostenstellen (1)' },
  { type: 2, label: 'Kostenträger (2)' },
];

const costCenterColumns = [
  { key: 'cnAccountNumber', label: 'Nr. (official)', type: 'text', required: true, placeholder: '10130', width: '130px' },
  { key: 'label', label: 'Label', type: 'text', required: true, placeholder: 'Label' },
  { key: 'lanoAccountNumber', label: 'Lano Nr.', type: 'text', placeholder: 'Lano Nr.', width: '130px' },
  { key: 'lanoId', label: 'Lano id', type: 'number', placeholder: 'Lano id', width: '120px' },
  {
    key: 'lanoStatus',
    label: 'Lano',
    type: 'readonly',
    width: '110px',
    render: (row) => (trimOrNull(row.lanoAccountNumber) ? 'in Lano' : 'not in Lano'),
  },
];

const normaliseCostCenter = (key, value) => (key === 'lanoId' ? numberOrNull(value) : trimOrNull(value));

function CostCenterList({ type }) {
  const { refresh } = useCostCenters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await costCenterService.getAll({ type, includeInactive: true });
      setRows((res.data?.costCenters || []).slice().sort(bySort));
    } catch (err) {
      setError(apiError(err, 'Could not load the cost centers'));
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  // The rest of the app reads cost centers from the context, so it must be refreshed too.
  const afterChange = async () => {
    await load();
    refresh();
  };

  const handleCreate = async (values) => {
    await costCenterService.create({
      type,
      label: String(values.label).trim(),
      cnAccountNumber: String(values.cnAccountNumber).trim(),
      lanoAccountNumber: trimOrNull(values.lanoAccountNumber),
      lanoId: numberOrNull(values.lanoId),
    });
    await afterChange();
  };

  const handleUpdate = async (row, values) => {
    const partial = changedOnly(row, values, normaliseCostCenter);
    if (Object.keys(partial).length === 0) return;
    await costCenterService.update(row.id, partial);
    await afterChange();
  };

  const handleDeactivate = async (row) => {
    await costCenterService.deactivate(row.id); // soft: sets isActive = false, nothing is removed
    await afterChange();
  };

  const handleReactivate = async (row) => {
    await costCenterService.reactivate(row.id);
    await afterChange();
  };

  return (
    <EditableList
      id={`cost-centers-${type}`}
      columns={costCenterColumns}
      rows={rows}
      loading={loading}
      error={error}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDeactivate={handleDeactivate}
      onReactivate={handleReactivate}
      addLabel="Add cost center"
      emptyText="No cost centers of this type yet."
    />
  );
}

function CostCentersTab() {
  const [type, setType] = useState(1);
  return (
    <>
      <p className="settings-intro">
        Official numbers print on the credit note (KST columns) and go to ELO. Entries are never
        deleted: deactivating hides them from the dropdowns while old bookings keep their values.
      </p>
      <div className="settings-subtabs" role="tablist">
        {COST_CENTER_TYPES.map((t) => (
          <button
            key={t.type}
            type="button"
            role="tab"
            id={`settings-subtab-cost-centers-${t.type}`}
            className={`settings-subtab${type === t.type ? ' active' : ''}`}
            aria-selected={type === t.type}
            onClick={() => setType(t.type)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* keyed so the list state (edit row, search) resets when switching type */}
      <CostCenterList key={type} type={type} />
    </>
  );
}

// ── Order clients ─────────────────────────────────────────────────────────────

const ORDER_TYPES = [
  { type: 'kind', label: 'KIND' },
  { type: 'copywrite', label: 'Copywrite' },
];

const costCenterOption = (c) => ({ value: c.id, label: `${c.label} (${c.cnAccountNumber || c.accountNumber || '—'})` });

function OrderClientList({ orderType }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options1, setOptions1] = useState([]);
  const [options2, setOptions2] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Active cost centers only: an inactive one must not be offered as a new default, but a
      // client that still points to one keeps working (the id simply shows as "—" here).
      const [clientsRes, cc1Res, cc2Res] = await Promise.all([
        orderClientService.getAll({ type: orderType, includeInactive: true }),
        costCenterService.getAll({ type: 1 }),
        costCenterService.getAll({ type: 2 }),
      ]);
      const clients = (clientsRes.data?.clients || []).slice().sort(bySort).map((client) => ({
        ...client,
        // The list edits ids; the API returns the nested cost center objects.
        defaultCostCenter1Id: client.defaultCostCenter1?.id ?? '',
        defaultCostCenter2Id: client.defaultCostCenter2?.id ?? '',
      }));
      setRows(clients);
      setOptions1((cc1Res.data?.costCenters || []).filter((c) => c.isActive !== false).sort(bySort).map(costCenterOption));
      setOptions2((cc2Res.data?.costCenters || []).filter((c) => c.isActive !== false).sort(bySort).map(costCenterOption));
    } catch (err) {
      setError(apiError(err, 'Could not load the clients'));
    } finally {
      setLoading(false);
    }
  }, [orderType]);

  useEffect(() => { load(); }, [load]);

  const emptyOption = { value: '', label: '—' };

  const columns = [
    { key: 'label', label: 'Label', type: 'text', required: true, placeholder: 'Client name' },
    {
      key: 'value',
      label: 'Slug',
      type: 'readonly',
      width: '160px',
      render: (row) => <code className="settings-code">{row.value}</code>,
      addHint: 'generated from the label',
    },
    { key: 'defaultCostCenter1Id', label: 'Default Kostenstelle', type: 'select', options: [emptyOption, ...options1], width: '240px' },
    { key: 'defaultCostCenter2Id', label: 'Default Kostenträger', type: 'select', options: [emptyOption, ...options2], width: '240px' },
  ];

  const normaliseClient = (key, value) => (key === 'label' ? trimOrNull(value) : (value === '' || value === null || value === undefined ? null : String(value)));

  const handleCreate = async (values) => {
    await orderClientService.create({
      orderType,
      label: String(values.label).trim(),
      // the slug (`value`) is derived by the backend from the label
      defaultCostCenter1Id: normaliseClient('defaultCostCenter1Id', values.defaultCostCenter1Id),
      defaultCostCenter2Id: normaliseClient('defaultCostCenter2Id', values.defaultCostCenter2Id),
    });
    await load();
  };

  const handleUpdate = async (row, values) => {
    const partial = changedOnly(row, values, normaliseClient);
    if (Object.keys(partial).length === 0) return;
    await orderClientService.update(row.id, partial);
    await load();
  };

  const handleDeactivate = async (row) => {
    await orderClientService.deactivate(row.id); // soft: isActive = false
    await load();
  };

  const handleReactivate = async (row) => {
    await orderClientService.reactivate(row.id);
    await load();
  };

  return (
    <EditableList
      id={`order-clients-${orderType}`}
      columns={columns}
      rows={rows}
      loading={loading}
      error={error}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDeactivate={handleDeactivate}
      onReactivate={handleReactivate}
      addLabel="Add client"
      emptyText="No clients for this order type yet."
    />
  );
}

function OrderClientsTab() {
  const [orderType, setOrderType] = useState('kind');
  return (
    <>
      <p className="settings-intro">
        Clients offered in Create KIND Order / Create Copywrite Order. The default cost centers are
        copied onto every new order for that client and can still be changed when proposing a talent.
      </p>
      <div className="settings-subtabs" role="tablist">
        {ORDER_TYPES.map((t) => (
          <button
            key={t.type}
            type="button"
            role="tab"
            id={`settings-subtab-order-clients-${t.type}`}
            className={`settings-subtab${orderType === t.type ? ' active' : ''}`}
            aria-selected={orderType === t.type}
            onClick={() => setOrderType(t.type)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <OrderClientList key={orderType} orderType={orderType} />
    </>
  );
}

// ── Sheets export ─────────────────────────────────────────────────────────────

function SheetsExportTab() {
  return (
    <div className="settings-panel">
      <p className="settings-intro">
        The nightly Google Sheets export (service account key, target spreadsheets, test and
        manual runs) has its own settings page.
      </p>
      <Link to="/admin/sheets-export" className="big-button settings-link-button" id="settings-open-sheets-export">
        Open Sheets Export settings
      </Link>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function SettingsPage() {
  const [activeTab, setActiveTab] = useState(readStoredTab);

  const selectTab = (key) => {
    setActiveTab(key);
    storeTab(key);
  };

  return (
    <div className="content-wrapper page">
      <section className="settings-page">
        <h1 className="h1">Settings</h1>

        <div className="settings-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`settings-tab-${t.key}`}
              className={`settings-tab${activeTab === t.key ? ' active' : ''}`}
              aria-selected={activeTab === t.key}
              onClick={() => selectTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'cost-centers' && <CostCentersTab />}
        {activeTab === 'order-clients' && <OrderClientsTab />}
        {activeTab === 'sheets-export' && <SheetsExportTab />}
      </section>
    </div>
  );
}

export default SettingsPage;
